/**
 * Component Parser Registry
 *
 * Central registry that maps component types to their parsers.
 * This allows the page parser to dynamically dispatch to the correct parser.
 */

import type { IBody, IBodySettings } from '@makyoapp/makyo-sdk/models/AppBuilder'

// Button components
import * as Button from './button/button'
import * as Fab from './button/fab'
import * as Icon from './button/icon'
// Chart components
import * as BarChart from './chart/bar-chart'
import * as PieChart from './chart/pie-chart'
// Container components
import { createFormStructure } from './container/form'
import * as Form from './container/form/form.comp'
import * as Section from './container/section'
import { createTabsStructure } from './container/tabs'
import * as Tabs from './container/tabs/tabs.comp'
// Form field components
import * as Checkbox from './form/checkbox/checkbox.comp'
import * as DateTimePicker from './form/datetime-picker/datetime-picker.comp'
import * as FieldRating from './form/field-rating/field-rating.comp'
import * as File from './form/file/file.comp'
import * as HiddenField from './form/hidden-field/hidden-field.comp'
import * as IconPicker from './form/icon-picker/icon-picker.comp'
import * as ImageUpload from './form/image-upload/image-upload.comp'
import * as PhoneInput from './form/phone-input/phone-input.comp'
import * as Selection from './form/selection/selection.comp'
import * as Slider from './form/slider/slider.comp'
import * as Switch from './form/switch/switch.comp'
import * as TextField from './form/text-field/text-field.comp'
// Interactive components
import * as Comment from './interactive/comment'
import * as Rating from './interactive/rating'
// List components
import * as Accordion from './list/accordion'
import * as Card from './list/card'
import * as Gallery from './list/gallery'
import * as List from './list/list'
import * as ShoppingCart from './list/shopping-cart'
import * as Timeline from './list/timeline'
// Media components
import * as Audio from './media/audio'
import * as Image from './media/image'
import * as Video from './media/video'
// Progress components
import * as CircularProgress from './progress/circular-progress'
import * as LinearProgress from './progress/linear-progress'
// Table components
import * as DataTable from './table/data-table'
import * as SingleRow from './table/single-row'
// Text components
import * as RichText from './text/rich-text'
import * as Text from './text/text'
// Utility components
import * as Alert from './utility/alert'
import * as Calendar from './utility/calendar'
import * as LoginForm from './utility/login-form'
import * as Newsletter from './utility/newsletter'
import * as Pricing from './utility/pricing'
import * as Prompt from './utility/prompt'
import * as Quantity from './utility/quantity'
import * as VerticalMenu from './utility/vertical-menu'
// Visual components
import * as Divider from './visual/divider'
import * as Map from './visual/map'

/**
 * Base AI component input
 */
export interface BaseAiComponentInput {
  type: string // 'section', 'text', 'button', etc.
  id?: string // Optional custom ID
  title?: string // Optional custom title for the component
  children?: AiComponentInput[] // Nested children (for containers)
}

/**
 * Union type of all AI component inputs
 */
export type AiComponentInput =
  | (BaseAiComponentInput & { type: 'section' } & Section.AiSectionInput)
  | (BaseAiComponentInput & { type: 'tabs' } & Tabs.AiTabsInput)
  | (BaseAiComponentInput & { type: 'form' } & Form.AiFormInput)
  | (BaseAiComponentInput & { type: 'text' } & Text.AiTextInput)
  | (BaseAiComponentInput & { type: 'rich-text' } & RichText.AiRichTextInput)
  | (BaseAiComponentInput & { type: 'button' } & Button.AiButtonInput)
  | (BaseAiComponentInput & { type: 'fab' } & Fab.AiFabInput)
  | (BaseAiComponentInput & { type: 'image' } & Image.AiImageInput)
  | (BaseAiComponentInput & { type: 'video' } & Video.AiVideoInput)
  | (BaseAiComponentInput & { type: 'audio' } & Audio.AiAudioInput)
  | (BaseAiComponentInput & { type: 'map' } & Map.AiMapInput)
  | (BaseAiComponentInput & { type: 'alert' } & Alert.AiAlertInput)
  | (BaseAiComponentInput & { type: 'newsletter' } & Newsletter.AiNewsletterInput)
  | (BaseAiComponentInput & { type: 'list' } & List.AiListInput)
  | (BaseAiComponentInput & { type: 'card' } & Card.AiCardInput)
  | (BaseAiComponentInput & { type: 'gallery' } & Gallery.AiGalleryInput)
  | (BaseAiComponentInput & { type: 'accordion' } & Accordion.AiAccordionInput)
  | (BaseAiComponentInput & { type: 'timeline' } & Timeline.AiTimelineInput)
  | (BaseAiComponentInput & { type: 'shopping-cart' } & ShoppingCart.AiShoppingCartInput)
  | (BaseAiComponentInput & { type: 'data-table' } & DataTable.AiDataTableInput)
  | (BaseAiComponentInput & { type: 'data-list' } & SingleRow.AiSingleRowInput)
  | (BaseAiComponentInput & { type: 'comment' } & Comment.AiCommentInput)
  | (BaseAiComponentInput & { type: 'rating' } & Rating.AiRatingInput)
  | (BaseAiComponentInput & { type: 'calendar' } & Calendar.AiCalendarInput)
  | (BaseAiComponentInput & { type: 'login-form' } & LoginForm.AiLoginFormInput)
  | (BaseAiComponentInput & { type: 'pricing' } & Pricing.AiPricingInput)
  | (BaseAiComponentInput & { type: 'prompt' } & Prompt.AiPromptInput)
  | (BaseAiComponentInput & { type: 'quantity' } & Quantity.AiQuantityInput)
  | (BaseAiComponentInput & { type: 'vertical-menu' } & VerticalMenu.AiVerticalMenuInput)
  | (BaseAiComponentInput & { type: 'divider' } & Divider.AiDividerInput)
  | (BaseAiComponentInput & { type: 'circular-progress' } & CircularProgress.AiCircularProgressInput)
  | (BaseAiComponentInput & { type: 'linear-progress' } & LinearProgress.AiLinearProgressInput)
  | (BaseAiComponentInput & { type: 'icon' } & Icon.AiIconInput)
  | (BaseAiComponentInput & { type: 'bar' } & BarChart.AiBarChartInput)
  | (BaseAiComponentInput & { type: 'pie' } & PieChart.AiPieChartInput)
  | (BaseAiComponentInput & { type: 'text-field' } & TextField.AiTextFieldInput)
  | (BaseAiComponentInput & { type: 'datetime-picker' } & DateTimePicker.AiDateTimePickerInput)
  | (BaseAiComponentInput & { type: 'switch' } & Switch.AiSwitchInput)
  | (BaseAiComponentInput & { type: 'selection' } & Selection.AiSelectionInput)
  | (BaseAiComponentInput & { type: 'image-upload' } & ImageUpload.AiImageUploadInput)
  | (BaseAiComponentInput & { type: 'phone-input' } & PhoneInput.AiPhoneInputInput)
  | (BaseAiComponentInput & { type: 'file' } & File.AiFileInput)
  | (BaseAiComponentInput & { type: 'field-rating' } & FieldRating.AiFieldRatingInput)
  | (BaseAiComponentInput & { type: 'icon-picker' } & IconPicker.AiIconPickerInput)
  | (BaseAiComponentInput & { type: 'checkbox' } & Checkbox.AiCheckboxInput)
  | (BaseAiComponentInput & { type: 'slider' } & Slider.AiSliderInput)
  | (BaseAiComponentInput & { type: 'hidden-field' } & HiddenField.AiHiddenFieldInput)
// Add more as they're created

/**
 * Component settings union type (the output)
 */
export type ComponentSettings =
  | Section.ISectionComponentSettings
  | Tabs.ITabsComponentSettings
  | Form.IFormComponentSettings
  | Text.ITextComponentSettings
  | RichText.IRichTextComponentSettings
  | Button.IButtonComponentSettings
  | Fab.IFabComponentSettings
  | Image.IImageComponentSettings
  | Video.IVideoComponentSettings
  | Audio.IAudioComponentSettings
  | Map.IMapComponentSettings
  | Alert.IAlertComponentSettings
  | Newsletter.INewsletterComponentSettings
  | List.IListComponentSettings
  | Card.ICardComponentSettings
  | Gallery.IGalleryComponentSettings
  | Accordion.IAccordionComponentSettings
  | Timeline.ITimelineComponentSettings
  | ShoppingCart.IShoppingCartComponentSettings
  | DataTable.IDataTableComponentSettings
  | SingleRow.ISingleRowComponentSettings
  | Comment.ICommentComponentSettings
  | Rating.IRatingComponentSettings
  | Calendar.ICalendarComponentSettings
  | LoginForm.ILoginFormComponentSettings
  | Pricing.IPricingComponentSettings
  | Prompt.IPromptComponentSettings
  | Quantity.IQuantityComponentSettings
  | VerticalMenu.IVerticalMenuComponentSettings
  | Divider.IDividerComponentSettings
  | CircularProgress.ICircularProgressComponentSettings
  | LinearProgress.ILinearProgressComponentSettings
  | Icon.IIconComponentSettings
  | BarChart.IBarChartComponentSettings
  | PieChart.IPieChartComponentSettings
  | TextField.ITextFieldComponentSettings
  | DateTimePicker.IDateTimePickerComponentSettings
  | Switch.ISwitchComponentSettings
  | Selection.ISelectionComponentSettings
  | ImageUpload.IImageUploadComponentSettings
  | PhoneInput.IPhoneInputComponentSettings
  | File.IFileComponentSettings
  | FieldRating.IFieldRatingComponentSettings
  | IconPicker.IIconPickerComponentSettings
  | Checkbox.ICheckboxComponentSettings
  | Slider.ISliderComponentSettings
  | HiddenField.IHiddenFieldComponentSettings
// Add more as they're created

/**
 * Parser function type signature (returns settings only)
 */
export type ComponentParser<TInput = any, TOutput = any> = (input: TInput) => TOutput

/**
 * Special parser function type (returns full body structure)
 * Used for components that need to generate custom child structures (like tabs -> tab-items)
 */
export type SpecialComponentParser = (
  input: any,
  bodySettings: any,
  parseChild: (childInput: AiComponentInput, settings: any) => any
) => any

/**
 * Component Parser Registry
 * Maps component type to its parser function (returns settings only)
 */
export const COMPONENT_PARSER_REGISTRY: Record<string, ComponentParser> = {
  // Container components
  ['section']: (input: Section.AiSectionInput) => new Section.SectionComp().parse(input),

  // Text components
  ['text']: (input: Text.AiTextInput) => new Text.TextComp().parse(input),
  ['rich-text']: (input: RichText.AiRichTextInput) => new RichText.RichTextComp().parse(input),

  // Button components
  ['button']: (input: Button.AiButtonInput) => new Button.ButtonComp().parse(input),
  ['fab']: (input: Fab.AiFabInput) => new Fab.FabComp().parse(input),
  ['icon']: (input: Icon.AiIconInput) => new Icon.IconComp().parse(input),

  // Media components
  ['image']: (input: Image.AiImageInput) => new Image.ImageComp().parse(input),
  ['video']: (input: Video.AiVideoInput) => new Video.VideoComp().parse(input),
  ['audio']: (input: Audio.AiAudioInput) => new Audio.AudioComp().parse(input),

  // Visual components
  ['map']: (input: Map.AiMapInput) => new Map.MapComp().parse(input),
  ['divider']: (input: Divider.AiDividerInput) => new Divider.DividerComp().parse(input),

  // Utility components
  ['alert']: (input: Alert.AiAlertInput) => new Alert.AlertComp().parse(input),
  ['calendar']: (input: Calendar.AiCalendarInput) => new Calendar.CalendarComp().parse(input),
  ['login-form']: (input: LoginForm.AiLoginFormInput) => new LoginForm.LoginFormComp().parse(input),
  ['newsletter']: (input: Newsletter.AiNewsletterInput) => new Newsletter.NewsletterComp().parse(input),
  ['pricing']: (input: Pricing.AiPricingInput) => new Pricing.PricingComp().parse(input),
  ['prompt']: (input: Prompt.AiPromptInput) => new Prompt.PromptComp().parse(input),
  ['quantity']: (input: Quantity.AiQuantityInput) => new Quantity.QuantityComp().parse(input),
  ['vertical-menu']: (input: VerticalMenu.AiVerticalMenuInput) => new VerticalMenu.VerticalMenuComp().parse(input),

  // List components
  ['list']: (input: List.AiListInput) => new List.ListComp().parse(input),
  ['card']: (input: Card.AiCardInput) => new Card.CardComp().parse(input),
  ['gallery']: (input: Gallery.AiGalleryInput) => new Gallery.GalleryComp().parse(input),
  ['accordion']: (input: Accordion.AiAccordionInput) => new Accordion.AccordionComp().parse(input),
  ['timeline']: (input: Timeline.AiTimelineInput) => new Timeline.TimelineComp().parse(input),
  ['shopping-cart']: (input: ShoppingCart.AiShoppingCartInput) => new ShoppingCart.ShoppingCartComp().parse(input),

  // Table components
  ['data-table']: (input: DataTable.AiDataTableInput) => new DataTable.DataTableComp().parse(input),
  ['data-list']: (input: SingleRow.AiSingleRowInput) => new SingleRow.SingleRowComp().parse(input),

  // Interactive components
  ['comment']: (input: Comment.AiCommentInput) => new Comment.CommentComp().parse(input),
  ['rating']: (input: Rating.AiRatingInput) => new Rating.RatingComp().parse(input),

  // Progress components
  ['circular-progress']: (input: CircularProgress.AiCircularProgressInput) =>
    new CircularProgress.CircularProgressComp().parse(input),
  ['linear-progress']: (input: LinearProgress.AiLinearProgressInput) =>
    new LinearProgress.LinearProgressComp().parse(input),

  // Chart components
  ['bar']: (input: BarChart.AiBarChartInput) => new BarChart.BarChartComp().parse(input),
  ['pie']: (input: PieChart.AiPieChartInput) => new PieChart.PieChartComp().parse(input),

  // Form field components (needed when form fields appear inside sections)
  ['text-field']: (input: TextField.AiTextFieldInput) => new TextField.TextFieldComp().parse(input),
  ['selection']: (input: Selection.AiSelectionInput) => new Selection.SelectionComp().parse(input),
  ['checkbox']: (input: Checkbox.AiCheckboxInput) => new Checkbox.CheckboxComp().parse(input),
  ['switch']: (input: Switch.AiSwitchInput) => new Switch.SwitchComp().parse(input),
  ['slider']: (input: Slider.AiSliderInput) => new Slider.SliderComp().parse(input),
  ['datetime-picker']: (input: DateTimePicker.AiDateTimePickerInput) =>
    new DateTimePicker.DateTimePickerComp().parse(input),
  ['file']: (input: File.AiFileInput) => new File.FileComp().parse(input),
  ['image-upload']: (input: ImageUpload.AiImageUploadInput) => new ImageUpload.ImageUploadComp().parse(input),
  ['phone-input']: (input: PhoneInput.AiPhoneInputInput) => new PhoneInput.PhoneInputComp().parse(input),
  ['field-rating']: (input: FieldRating.AiFieldRatingInput) => new FieldRating.FieldRatingComp().parse(input),
  ['hidden-field']: (input: HiddenField.AiHiddenFieldInput) => new HiddenField.HiddenFieldComp().parse(input),
  ['icon-picker']: (input: IconPicker.AiIconPickerInput) => new IconPicker.IconPickerComp().parse(input),

  // Add more parsers as they're created
}

/**
 * Special Component Parser Registry
 * For components that need custom body structure generation (e.g., tabs with tab-items)
 * These parsers handle both settings AND body structure creation
 */
export const SPECIAL_COMPONENT_PARSERS: Record<string, SpecialComponentParser> = {
  // Tabs generates tab-item children automatically
  tabs: (input, bodySettings, parseChild) => {
    return createTabsStructure(input, bodySettings, parseChild)
  },
  // Form generates field children automatically
  form: (input, bodySettings, parseChild) => {
    return createFormStructure(input, bodySettings, parseChild)
  },
  // Add more special parsers as needed
}

/**
 * Check if component type requires special parsing
 */
export function isSpecialComponent(type: string): boolean {
  return type in SPECIAL_COMPONENT_PARSERS
}

/**
 * Parse a single component based on its type
 * Note: This only works for normal components. Special components should use the parser.ts flow.
 */
export function parseAiCompSettings(input: AiComponentInput): ComponentSettings {
  const parser = COMPONENT_PARSER_REGISTRY[input.type]

  if (!parser) {
    throw new Error(`No parser registered for component type: ${input.type}`)
  }

  // Call the appropriate parser
  return parser(input)
}

/**
 * Component Format Registry
 * Maps component type to its format function (converts settings back to input)
 * This is the reverse operation of parsing.
 *
 * Note: Format methods will be implemented incrementally.
 * Components without format methods will throw "Not implemented" errors.
 */
export const COMPONENT_FORMAT_REGISTRY: Record<string, (settings: any) => AiComponentInput> = {
  // Container components
  ['section']: (settings: Section.ISectionComponentSettings) => ({
    ...new Section.SectionComp().format(settings),
    type: 'section',
  }),
  // Note: 'form' and 'tabs' are handled by SPECIAL_COMPONENT_FORMATTERS

  // Text components
  ['text']: (settings: Text.ITextComponentSettings) => ({ ...new Text.TextComp().format(settings), type: 'text' }),
  ['rich-text']: (settings: RichText.IRichTextComponentSettings) => ({
    ...new RichText.RichTextComp().format(settings),
    type: 'rich-text',
  }),

  // Button components
  ['button']: (settings: Button.IButtonComponentSettings) => ({
    ...new Button.ButtonComp().format(settings),
    type: 'button',
  }),
  ['fab']: (settings: Fab.IFabComponentSettings) => ({ ...new Fab.FabComp().format(settings), type: 'fab' }),
  ['icon']: (settings: Icon.IIconComponentSettings) => ({ ...new Icon.IconComp().format(settings), type: 'icon' }),

  // Media components
  ['image']: (settings: Image.IImageComponentSettings) => ({
    ...new Image.ImageComp().format(settings),
    type: 'image',
  }),
  ['video']: (settings: Video.IVideoComponentSettings) => ({
    ...new Video.VideoComp().format(settings),
    type: 'video',
  }),
  ['audio']: (settings: Audio.IAudioComponentSettings) => ({
    ...new Audio.AudioComp().format(settings),
    type: 'audio',
  }),

  // Visual components
  ['map']: (settings: Map.IMapComponentSettings) => ({ ...new Map.MapComp().format(settings), type: 'map' }),
  ['divider']: (settings: Divider.IDividerComponentSettings) => ({
    ...new Divider.DividerComp().format(settings),
    type: 'divider',
  }),

  // Utility components
  ['alert']: (settings: Alert.IAlertComponentSettings) => ({ ...new Alert.AlertComp().format(settings), type: 'alert' }),
  ['calendar']: (settings: Calendar.ICalendarComponentSettings) => ({ ...new Calendar.CalendarComp().format(settings), type: 'calendar' }),
  ['login-form']: (settings: LoginForm.ILoginFormComponentSettings) => ({ ...new LoginForm.LoginFormComp().format(settings), type: 'login-form' }),
  ['newsletter']: (settings: Newsletter.INewsletterComponentSettings) => ({ ...new Newsletter.NewsletterComp().format(settings), type: 'newsletter' }),
  ['pricing']: (settings: Pricing.IPricingComponentSettings) => ({ ...new Pricing.PricingComp().format(settings), type: 'pricing' }),
  ['prompt']: (settings: Prompt.IPromptComponentSettings) => ({ ...new Prompt.PromptComp().format(settings), type: 'prompt' }),
  ['quantity']: (settings: Quantity.IQuantityComponentSettings) => ({ ...new Quantity.QuantityComp().format(settings), type: 'quantity' }),
  ['vertical-menu']: (settings: VerticalMenu.IVerticalMenuComponentSettings) => ({ ...new VerticalMenu.VerticalMenuComp().format(settings), type: 'vertical-menu' }),

  // Table components
  ['data-table']: (settings: DataTable.IDataTableComponentSettings) => ({ ...new DataTable.DataTableComp().format(settings), type: 'data-table' }),
  ['data-list']: (settings: SingleRow.ISingleRowComponentSettings) => ({ ...new SingleRow.SingleRowComp().format(settings), type: 'data-list' }),

  // List components
  ['list']: (settings: List.IListComponentSettings) => ({ ...new List.ListComp().format(settings), type: 'list' }),
  ['card']: (settings: Card.ICardComponentSettings) => ({ ...new Card.CardComp().format(settings), type: 'card' }),
  ['gallery']: (settings: Gallery.IGalleryComponentSettings) => ({ ...new Gallery.GalleryComp().format(settings), type: 'gallery' }),
  ['accordion']: (settings: Accordion.IAccordionComponentSettings) => ({ ...new Accordion.AccordionComp().format(settings), type: 'accordion' }),
  ['timeline']: (settings: Timeline.ITimelineComponentSettings) => ({ ...new Timeline.TimelineComp().format(settings), type: 'timeline' }),
  ['shopping-cart']: (settings: ShoppingCart.IShoppingCartComponentSettings) => ({ ...new ShoppingCart.ShoppingCartComp().format(settings), type: 'shopping-cart' }),



  // Interactive components
  ['comment']: (settings: Comment.ICommentComponentSettings) => ({ ...new Comment.CommentComp().format(settings), type: 'comment' }),
  ['rating']: (settings: Rating.IRatingComponentSettings) => ({ ...new Rating.RatingComp().format(settings), type: 'rating' }),

  // Progress components
  ['circular-progress']: (settings: CircularProgress.ICircularProgressComponentSettings) => ({ ...new CircularProgress.CircularProgressComp().format(settings), type: 'circular-progress' }),
  ['linear-progress']: (settings: LinearProgress.ILinearProgressComponentSettings) => ({ ...new LinearProgress.LinearProgressComp().format(settings), type: 'linear-progress' }),

  // Chart components
  ['bar']: (settings: BarChart.IBarChartComponentSettings) => ({
    ...new BarChart.BarChartComp().format(settings),
    type: 'bar',
  }),
  ['pie']: (settings: PieChart.IPieChartComponentSettings) => ({
    ...new PieChart.PieChartComp().format(settings),
    type: 'pie',
  }),

  // Form field components
  ['text-field']: (settings: TextField.ITextFieldComponentSettings) => ({
    ...new TextField.TextFieldComp().format(settings),
    type: 'text-field',
  }),
  ['datetime-picker']: (settings: DateTimePicker.IDateTimePickerComponentSettings) => ({
    ...new DateTimePicker.DateTimePickerComp().format(settings),
    type: 'datetime-picker',
  }),
  ['switch']: (settings: Switch.ISwitchComponentSettings) => ({
    ...new Switch.SwitchComp().format(settings),
    type: 'switch',
  }),
  ['selection']: (settings: Selection.ISelectionComponentSettings) => ({
    ...new Selection.SelectionComp().format(settings),
    type: 'selection',
  }),
  ['image-upload']: (settings: ImageUpload.IImageUploadComponentSettings) => ({
    ...new ImageUpload.ImageUploadComp().format(settings),
    type: 'image-upload',
  }),
  ['phone-input']: (settings: PhoneInput.IPhoneInputComponentSettings) => ({
    ...new PhoneInput.PhoneInputComp().format(settings),
    type: 'phone-input',
  }),
  ['file']: (settings: File.IFileComponentSettings) => ({ ...new File.FileComp().format(settings), type: 'file' }),
  ['field-rating']: (settings: FieldRating.IFieldRatingComponentSettings) => ({
    ...new FieldRating.FieldRatingComp().format(settings),
    type: 'field-rating',
  }),
  ['icon-picker']: (settings: IconPicker.IIconPickerComponentSettings) => ({
    ...new IconPicker.IconPickerComp().format(settings),
    type: 'icon-picker',
  }),
  ['checkbox']: (settings: Checkbox.ICheckboxComponentSettings) => ({
    ...new Checkbox.CheckboxComp().format(settings),
    type: 'checkbox',
  }),
  ['slider']: (settings: Slider.ISliderComponentSettings) => ({
    ...new Slider.SliderComp().format(settings),
    type: 'slider',
  }),
  ['hidden-field']: (settings: HiddenField.IHiddenFieldComponentSettings) => ({
    ...new HiddenField.HiddenFieldComp().format(settings),
    type: 'hidden-field',
  }),

  // Add more formatters as they're created
}

/**
 * Special Component Formatter type
 * Used for components that need custom structure formatting (like tabs, form)
 */
export type SpecialComponentFormatter = (
  body: IBody,
  settings: IBodySettings,
  formatChild: (child: IBody, settings: IBodySettings) => AiComponentInput
) => AiComponentInput

/**
 * Special Component Formatter Registry
 * For components that need custom body structure formatting (e.g., tabs with tab-items)
 */
export const SPECIAL_COMPONENT_FORMATTERS: Record<string, SpecialComponentFormatter> = {
  tabs: (body, settings, formatChild) => {
    const tabsSettings = settings[body._id] as Tabs.ITabsComponentSettings
    const tabsComp = new Tabs.TabsComp()
    const formatted = tabsComp.format(tabsSettings, body, settings, formatChild)
    const { type: _, ...restFormatted } = formatted as any
    return {
      type: 'tabs',
      id: body._id,
      ...restFormatted,
      ...(body._Title && { title: body._Title }),
    }
  },
  form: (body, settings, formatChild) => {
    const formSettings = settings[body._id] as Form.IFormComponentSettings
    const formComp = new Form.FormComp()
    const formatted = formComp.format(formSettings, body, settings, formatChild)
    const { type: _, ...restFormatted } = formatted as any
    return {
      type: 'form',
      id: body._id,
      ...restFormatted,
      ...(body._Title && { title: body._Title }),
    }
  },
  // Add more special formatters as they're created
}
